var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Switch, Dialog, DialogContent, FormControlLabel, DialogActions } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useContext, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import Loading from "../../../Shared/components/loading";
import { BillingInformationContext } from "../BillingInformationContext";
import { CardDialogTitle } from "../CardDialogTitle";
import { usePaymentMethodUpdate } from "../Shared/hooks/PaymentMethodUpdate/usePaymentMethodUpdate";
import { CardFormText, CardFormTitle, PaymentProcessor, StatusType, } from "../Shared/types/PaymentProcessorTypes";
import { Action, AUTH_FORM_ID, MODE } from "./AcceptHostedForm-types";
import { useAcceptHosted } from "./useAcceptHosted";
import { useAcceptHostedPost } from "./useAcceptHostedPost";
import { LoadingButton } from "@mui/lab";
import { useTheme } from "@mui/material";
import { CheckoutContext } from "../../../Checkout/CheckoutContext";
/**
 * useCardForm() - Hook to handle form state.
 * Only state on this form is the isPrimary flag
 */
const useCardForm = (card) => {
    var _a;
    const { control, getValues } = useForm({
        mode: "onChange",
    });
    const defaultValues = {
        isPrimaryChecked: (_a = card === null || card === void 0 ? void 0 : card.isPrimary) !== null && _a !== void 0 ? _a : false,
    };
    return { control, defaultValues, getValues };
};
/**
 * AcceptHostedForm() - Authorize.net hosted iframe form uses a formToken fetched from our server to generate a form for the customer.
 * Uses an iframeCommunicator (Auth.html) in order to communicate with this app
 * using window.CommunicationHandler.onReceiveCommunication
 *
 * NOTE - "form cannot appear as a descendant of form" so the AcceptHostedForm must be next to the form where its being used, not inside of it.
 * See [Checkout.tsx](../../../Checkout/Checkout.tsx) for example
 *
 * @param  props - formToken, title, style, children, card
 * @returns Authorize Hosted Form
 */
export const AcceptHostedForm = ({ formToken, style, children, selectedCard, url, showPrimaryCheckbox, setSelectedCard, canPayLater, }) => {
    var _a, _b, _c;
    const { AuthorizeNetPostUrl, iFrameOpen, setIFrameOpen, parseResponse, mobile, cardFormState, setCardFormState } = useAcceptHosted(url);
    const { handleUpdate, handleAdd, loading } = useAcceptHostedPost();
    const { initPaymentMethodUpdate, loading: initUpdateLoading } = usePaymentMethodUpdate();
    const { setAddedNewCard, paymentProfileId, setPaymentProfileId, setDismissedAddPaymentModal } = useContext(BillingInformationContext);
    const { control, defaultValues, getValues } = useCardForm(selectedCard);
    const { setIsPayLaterSelected } = useContext(CheckoutContext);
    //This is used to submit the Authorize form AFTER we fetch initialization data
    const formRef = useRef();
    window.CommunicationHandler = {};
    window.CommunicationHandler.onReceiveCommunication = (queryStr) => __awaiter(void 0, void 0, void 0, function* () {
        var _d, _e;
        const params = parseResponse(queryStr.qstr);
        switch (params["action"]) {
            case Action.RESIZE_WINDOW:
                break;
            case Action.SUCCESSFUL_SAVE:
                if (!!selectedCard) {
                    const paymentMethodRequestBody = {
                        isPrimary: (_d = getValues("isPrimaryChecked")) !== null && _d !== void 0 ? _d : false,
                        paymentMethodId: selectedCard.id,
                        paymentProcessor: PaymentProcessor.AUTHORIZE,
                    };
                    setCardFormState({
                        submitted: true,
                        status: { type: StatusType.PROCESSING, message: CardFormText.UPDATE_LOADING_TEXT },
                        loading: true,
                    });
                    const { success, message } = yield handleUpdate(paymentMethodRequestBody);
                    success
                        ? setCardFormState({
                            submitted: true,
                            status: { type: StatusType.SUCCEEDED, message: CardFormText.YOUR_CARD_WAS_UPDATED },
                            loading: false,
                        })
                        : setCardFormState({
                            submitted: true,
                            status: { type: StatusType.ERROR, message: message },
                            loading: false,
                        });
                }
                else {
                    setCardFormState({
                        submitted: true,
                        status: { type: StatusType.PROCESSING, message: CardFormText.ADD_LOADING_TEXT },
                        loading: false,
                    });
                    const { success, message } = yield handleAdd({
                        isPrimary: (_e = getValues("isPrimaryChecked")) !== null && _e !== void 0 ? _e : false,
                        paymentProcessor: PaymentProcessor.AUTHORIZE,
                    });
                    success
                        ? setCardFormState({
                            submitted: true,
                            status: { type: StatusType.SUCCEEDED, message: CardFormText.YOUR_CARD_WAS_ADDED },
                            loading: false,
                        })
                        : setCardFormState({
                            submitted: true,
                            status: { type: StatusType.ERROR, message: message },
                            loading: false,
                        });
                }
                break;
            case Action.CANCEL:
                setIFrameOpen(false);
                break;
        }
    });
    const onClose = () => {
        if (!loading && !cardFormState.submitted) {
            setIFrameOpen(false);
            setDismissedAddPaymentModal(true);
        }
        //If the form as already been submitted, fetch updated data
        if (!loading && cardFormState.submitted) {
            setAddedNewCard(true);
            setCardFormState({
                submitted: false,
                status: { type: StatusType.IDLE, message: undefined },
                loading: false,
            });
            setIFrameOpen(false);
            setDismissedAddPaymentModal(true);
        }
        setSelectedCard && setSelectedCard(undefined);
        setPaymentProfileId(undefined);
    };
    //TODO: Better handle the height for this iframe
    let iframeHeight = mobile ? "calc(100vh - 95px)" : "calc(75vh - 70px - 64px - 20px)";
    /**
     * Open the Dialog and fetch update initialization data. Once we have that we can submit the form.
     */
    const authorizeFormSubmit = () => {
        setIFrameOpen(true);
        initPaymentMethodUpdate(selectedCard === null || selectedCard === void 0 ? void 0 : selectedCard.id).then(() => {
            formRef.current.submit();
        });
    };
    const theme = useTheme();
    const handleCloseWithPayLaterClick = () => {
        setIsPayLaterSelected(true);
        setIFrameOpen(false);
    };
    return (_jsxs("div", Object.assign({ style: style }, { children: [_jsxs("form", Object.assign({ ref: formRef, id: AUTH_FORM_ID, onSubmit: (e) => {
                    //Updating requires a paymentProfileId so we have to init the update before submitting the form
                    if (!!selectedCard) {
                        e.preventDefault();
                        authorizeFormSubmit();
                    }
                    else {
                        //If were adding a card we can open the form on submit
                        setIFrameOpen(true);
                    }
                }, method: "POST", action: AuthorizeNetPostUrl[selectedCard ? MODE.EDIT : MODE.ADD], target: "iframeAuthorizeNet" }, { children: [_jsx("input", { name: "token", type: "hidden", value: formToken !== null && formToken !== void 0 ? formToken : "" }), paymentProfileId && _jsx("input", { type: "hidden", name: "paymentProfileId", value: paymentProfileId }), children] })), _jsxs(Dialog, Object.assign({ open: iFrameOpen, onClose: onClose, fullScreen: mobile, maxWidth: "xs", fullWidth: true }, { children: [_jsx(CardDialogTitle, { title: `${selectedCard ? CardFormTitle.UPDATE : CardFormTitle.ADD} Payment Method`, onClick: onClose }), _jsx(DialogContent, Object.assign({ style: { padding: 0 } }, { children: loading || initUpdateLoading || cardFormState.submitted ? (_jsx(Loading, { text: (_a = cardFormState === null || cardFormState === void 0 ? void 0 : cardFormState.status) === null || _a === void 0 ? void 0 : _a.message, success: ((_b = cardFormState === null || cardFormState === void 0 ? void 0 : cardFormState.status) === null || _b === void 0 ? void 0 : _b.type) === StatusType.SUCCEEDED, error: ((_c = cardFormState === null || cardFormState === void 0 ? void 0 : cardFormState.status) === null || _c === void 0 ? void 0 : _c.type) === StatusType.ERROR, viewportHeight: iframeHeight })) : (_jsxs(_Fragment, { children: [showPrimaryCheckbox && (_jsx(Controller, { name: "isPrimaryChecked", control: control, defaultValue: defaultValues.isPrimaryChecked, render: ({ field: { onChange, value } }) => (_jsx(FormControlLabel, { style: { marginLeft: "8%" }, control: _jsx(Switch, { checked: value, onChange: onChange, color: "primary" }), label: "Primary Card" })) })), _jsx("iframe", { style: { height: iframeHeight, overflow: "scroll", border: "none" }, id: "iframeAuthorizeNet", name: "iframeAuthorizeNet", frameBorder: 0, width: "100%" })] })) })), canPayLater && (_jsxs(DialogActions, Object.assign({ sx: {
                            paddingLeft: theme.spacing(4),
                        } }, { children: [_jsx(Typography, { children: CardFormText.PAY_LATER_MODAL_MESSAGE }), _jsx(LoadingButton, Object.assign({ size: "medium", color: "primary", onClick: handleCloseWithPayLaterClick, variant: "outlined", sx: {
                                    flexShrink: 0,
                                } }, { children: CardFormText.PAY_LATER_BUTTON_TEXT }))] })))] }))] })));
};
